// How customer payments (deposits) are applied to Priority's approvedInvoices.
//
// A deposit points at a job through `invoiceNumber` (= the job's WO, or the
// job id when the job has no WO — the same value ApprovedInvoices'
// getInvoiceNumber() uses). A recurring pest contract is ONE job, so every
// visit's invoice shares that WO. Summing every payment on the WO and
// comparing it to each invoice would mark the October invoice paid with the
// September check. So payments are applied like this:
//
//   1. A deposit with `approvedInvoiceId` pays exactly that invoice.
//   2. A deposit with `occurrenceKey` (canonical visit date) pays the invoice
//      for that visit.
//   3. Anything else on the WO pays that WO's invoices oldest visit first,
//      each up to its amount; any extra stays on the newest invoice.
//
// With only one invoice on a WO this is the same as "sum every payment on the
// WO", so one-time jobs behave exactly as before.

export const numberValue = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const cents = (v) => Math.round(numberValue(v) * 100);

/** Same key ApprovedInvoices.js uses (getInvoiceNumber). */
export const invoicePaymentKey = (invoice = {}) =>
  invoice?.wo ? String(invoice.wo).trim() : String(invoice?.invoiceNumber || invoice?.id || "").trim();

export const invoiceTotal = (invoice = {}) =>
  numberValue(invoice.amount || invoice.price || invoice.rate);

export const invoiceStatus = (invoice = {}) => {
  const raw = String(invoice?.status || "Draft").trim();
  const lower = raw.toLowerCase();
  if (lower === "sent") return "Sent";
  if (lower === "paid") return "Paid";
  return raw || "Draft";
};

export const isVoidInvoice = (invoice = {}) =>
  ["void", "voided", "canceled", "cancelled", "deleted"].includes(String(invoice.status || "").trim().toLowerCase());

const dateKeyOf = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (value?.toDate) return value.toDate().toISOString().slice(0, 10);
  if (typeof value === "object" && value.seconds) return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

/** The visit an invoice covers (canonical key when the invoice has one). */
export const invoiceVisitKey = (invoice = {}) =>
  dateKeyOf(
    invoice.occurrenceKey ||
      invoice.originalOccurrenceDate ||
      invoice.occurrenceDate ||
      invoice.dateOfService ||
      invoice.serviceDate ||
      invoice.invoiceDate
  );

const sortKey = (invoice) => `${invoiceVisitKey(invoice) || "9999"}|${dateKeyOf(invoice.createdAt) || ""}|${invoice.id}`;

/** Deposits that are real money (not $0 "invoice only" rows of a split check). */
const isMoney = (d) => !(d?.splitInvoiceOnly || d?.invoiceOnly);

/**
 * { [invoiceId]: amountPaid } for every invoice, from the full deposits list.
 */
export const allocateDepositsToInvoices = (invoices = [], deposits = []) => {
  const paid = {};
  const byKey = new Map();
  invoices.forEach((inv) => {
    if (!inv?.id) return;
    paid[inv.id] = 0;
    if (isVoidInvoice(inv)) return;
    const key = invoicePaymentKey(inv);
    if (!key) return;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(inv);
  });

  const depositsByKey = new Map();
  const byInvoiceId = new Map(invoices.map((i) => [i.id, i]));
  deposits.filter(isMoney).forEach((d) => {
    const amount = numberValue(d.amount);
    if (!amount) return;
    // 1. explicit invoice link
    if (d.approvedInvoiceId && byInvoiceId.has(d.approvedInvoiceId)) {
      paid[d.approvedInvoiceId] += amount;
      return;
    }
    const key = String(d.invoiceNumber || "").trim();
    if (!key) return;
    if (!depositsByKey.has(key)) depositsByKey.set(key, []);
    depositsByKey.get(key).push(d);
  });

  depositsByKey.forEach((list, key) => {
    const group = (byKey.get(key) || []).slice().sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    if (!group.length) return;
    if (group.length === 1) {
      paid[group[0].id] += list.reduce((s, d) => s + numberValue(d.amount), 0);
      return;
    }
    // 2. visit link
    const loose = [];
    list.forEach((d) => {
      const visit = dateKeyOf(d.occurrenceKey);
      const hit = visit ? group.find((inv) => invoiceVisitKey(inv) === visit) : null;
      if (hit) paid[hit.id] += numberValue(d.amount);
      else loose.push(d);
    });
    // 3. oldest visit first
    let pool = loose.reduce((s, d) => s + numberValue(d.amount), 0);
    group.forEach((inv, i) => {
      if (pool <= 0) return;
      const last = i === group.length - 1;
      const room = Math.max(0, invoiceTotal(inv) - paid[inv.id]);
      const take = last ? pool : Math.min(pool, room);
      paid[inv.id] += take;
      pool -= take;
    });
  });

  Object.keys(paid).forEach((id) => {
    paid[id] = Math.round(paid[id] * 100) / 100;
  });
  return paid;
};

export const isInvoiceCovered = (invoice, paidAmount) => {
  const total = invoiceTotal(invoice);
  if (!total) return invoiceStatus(invoice) === "Paid";
  return cents(paidAmount) >= cents(total);
};

/** Balance still owed on an invoice (never negative). */
export const invoiceBalance = (invoice, paidAmount) =>
  Math.max(0, Math.round((invoiceTotal(invoice) - numberValue(paidAmount)) * 100) / 100);

/**
 * Deposits' duplicate check treats "same property + amount + WO within 45
 * days" as a duplicate. A recurring contract is paid the same amount on the
 * same WO every month, so the visit is added to the WO for that check when the
 * payment names one. Same visit still matches; different visits never do.
 */
export const depositVisitTag = (d = {}) => dateKeyOf(d.occurrenceKey) || "";

export const withVisitInWO = (d = {}) => {
  const tag = depositVisitTag(d);
  return tag && d.invoiceNumber ? { ...d, invoiceNumber: `${d.invoiceNumber}@${tag}` } : d;
};
